import React, { useEffect } from "react";
import classnames from "classnames";
import { gql, useQuery } from "@apollo/client";
import { Helmet } from "react-helmet";
import { Loader, Dimmer, Message } from "semantic-ui-react";
import { withTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { NotFoundRoute } from "../../Common/routing/NotFound";
import { Redirect } from "../../Common/routing/Redirect";
import Styles from "./ContentPages.module.scss";
import { ContactRoute } from "../misc/ContentPageRoutes";
import { useLayout } from "../../LayoutContextAPI/useLayout";

type Props = {
  t: (key: string) => string; // Assuming `t` takes a string key and returns a string
  i18n: {
    language: string;
  };
};

type TranslationFunction = (key: string, options?: Record<string, unknown>) => string;

const GET_PAGES = gql`
  query PagesByName($page: String) {
    page: pageBy(uri: $page) {
      title
      content
      seo {
        metaDesc
      }
      name: title
    }
  }
`;

const getPage = (pathname: string) => {
  return pathname.substring(1);
};

function replaceVolksbank(content: string, t: TranslationFunction) {
  return content
    .replace(/service@remind.me/gi, t("common.contactOptions.email"))
    .replace(/030 \/ 403 666 150/gi, t("common.contactOptions.phone"))
    .replace(/tel:004930403666150/gi, t("common.contactOptions.phoneCall"));
}

const ContentPages = (props: Props) => {
  const { t, i18n } = props;
  const location = useLocation();
  const page = getPage(location.pathname);
  const replaceVolksbankContact = location.pathname === ContactRoute && i18n.language === "vBank";
  const { dispatch } = useLayout();

  useEffect(() => {
    dispatch({
      type: "SET_LAYOUT_STATE",
      payload: {
        header: { show: false, simple: false },
        sidenav: true,
        footer: true,
        breadcrumbs: true,
        pageType: "wide",
        bgColor: "#ffffff",
      },
    });
  }, [dispatch]);

  const graphqlResponse = useQuery(GET_PAGES, {
    variables: { page },
  });

  const { loading, error, data } = graphqlResponse;

  if (loading) {
    return (
      <div className="h-container p-static-page">
        <Dimmer active inverted>
          <Loader inverted content="Loading" />
        </Dimmer>
      </div>
    );
  }
  if (error) {
    return <Message negative>{t("common.error.loadingError")}</Message>;
  }
  if (!data || !data.page) {
    return <Redirect to={NotFoundRoute} />;
  }

  const { title, content, name, seo } = data.page;

  return (
    <div className={Styles.contentPagesContainer}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={seo?.metaDesc} />
      </Helmet>
      <section className={classnames(Styles.hContainer, "p-static-page")} key={name}>
        <header>
          <h1 className="a-headline" dangerouslySetInnerHTML={{ __html: title }} />
        </header>
        <div dangerouslySetInnerHTML={{ __html: replaceVolksbankContact ? replaceVolksbank(content, t) : content }} />
      </section>
    </div>
  );
};

export default withTranslation(["contentPages", "common"])(ContentPages);
